import {Component, inject, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Router} from '@angular/router';
import {of} from 'rxjs';
import {map} from 'rxjs/operators';
import {AppTableComponent} from '@shared/components/tables/app-table/app-table.component';
import {AppTableBodyTemplateDirective, AppTableHeaderTemplateDirective} from '@shared/components/tables/app-table/directives/app-table.directives';
import {AppTableHeaderColumnComponent} from '@shared/components/tables/app-table-header-column/app-table-header-column.component';
import {AppPaginationComponent} from '@shared/components/tables/app-pagination/app-pagination.component';
import {AppPaginationBaseAbstract} from '@shared/commons/pagination-base-abstract.utils';
import {TranslateModule} from '@ngx-translate/core';
import {CompaniesApiService} from "@core/services/api/companies-api.service";
import {IbgeApiService} from "@core/services/api/ibge-api.service";
import {AlertService} from "@core/services/alert.service";
import {AppInputComponent} from "@shared/components/forms/app-input/app-input.component";
import {AppSelectComponent} from "@shared/components/forms/app-select/app-select.component";
import {FormsModule} from "@angular/forms";
import {AppEmptyAlertMessageComponent} from "@shared/components/commons/app-empty-alert-message/app-empty-alert-message.component";
import {AppLoadingOverlayComponent} from "@shared/components/commons/app-loading-overlay/app-loading-overlay.component";
import {CpfCnpjPipe} from "@shared/pipes/app-cpfcnpj.pipe";
import {AppCurrencyComponent} from "@shared/components/forms/app-currency/app-currency.component";
import {copyToClipboard} from "@shared/commons/keyboard.commons";
import {AppPhoneComponent} from "@shared/components/commons/app-phone/app-phone.component";
import {AppDatePickerComponent} from "@shared/components/forms/app-datepicker/app-date-picker.component";
import {NgbModal, NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {AppFilterBarComponent} from "@shared/components/commons/app-filter/app-filter-bar/app-filter-bar.component";
import {AppDropdownFiltersComponent} from "@shared/components/commons/app-filter/app-dropdown-filters/app-dropdown-filters.component";
import {CampaignCreationDialogComponent} from "@features/companies/components/campaigns/campaign-creation-dialog/campaign-creation-dialog.component";
import {CompanyDetailsComponent} from "@features/companies/components/company-details/company-details.component";
import {CompanyDebtsComponent} from "@features/companies/components/company-debts/company-debts.component";
import {PersonTypeHelper} from "@core/models/person-type.enums";
import {NegotiationTypeHelper} from "@core/models/negotiation-type.helper";
import {AppSwitchComponent} from "@shared/components/forms/app-switch/app-switch.component";

@Component({
    selector: 'app-companies',
    standalone: true,
    imports: [
        CommonModule,
        AppTableComponent,
        AppTableHeaderTemplateDirective,
        AppTableBodyTemplateDirective,
        AppTableHeaderColumnComponent,
        AppPaginationComponent,
        TranslateModule,
        AppInputComponent,
        AppSelectComponent,
        FormsModule,
        AppEmptyAlertMessageComponent,
        AppLoadingOverlayComponent,
        CpfCnpjPipe,
        AppCurrencyComponent,
        AppPhoneComponent,
        AppDatePickerComponent,
        NgbTooltip,
        AppFilterBarComponent,
        AppDropdownFiltersComponent,
        AppSwitchComponent
    ],
    templateUrl: './companies-list.component.html',
    styleUrls: ['./companies-list.component.scss']
})
export class CompaniesListComponent extends AppPaginationBaseAbstract implements OnInit {

    private alertService = inject(AlertService);
    private companiesApiService = inject(CompaniesApiService);
    private ibgeApiService = inject(IbgeApiService);
    private modalService = inject(NgbModal);

    protected PersonTypeHelper = PersonTypeHelper;
    protected items: any[] = [];
    protected states$ = this.ibgeApiService.getStates().pipe(map((response: any) => response.data));
    protected cities$ = of([]);
    private lastStateLoaded: string | null = null;
    protected totalAvailableForCampaign: number = 0;

    protected companySizeOptions = [{
        name: 'Microempresa (ME)',
        value: 'ME'
    }, {
        name: 'Empresa de Pequeno Porte (EPP)',
        value: 'EPP'
    }, {
        name: 'Demais',
        value: 'DEMAIS'
    }];

    protected registrationStatusOptions = [{
        name: 'Ativa',
        value: 'ATIVA'
    }, {
        name: 'Inativa',
        value: 'INATIVA'
    }, {
        name: 'Baixada',
        value: 'BAIXADA'
    }];

    protected personTypeOptions = [{
        name: 'Pessoa Física',
        value: 'PF'
    }, {
        name: 'Pessoa Jurídica',
        value: 'PJ'
    }];

    protected negotiationTypeOptions = NegotiationTypeHelper.getOptions();

    constructor(private router: Router) {
        super();
    }

    override onInit(): void {
        this.addCustomSort([{field: 'name', direction: 'asc'}]);
        this.addCustomFilters({
            excludeRescindedTransacaoRepactuacaoUpTo26Overdues: false
        });
    }

    override normalizeQueryParams(params: any): any {
        // Normalize array fields - ensure they're always arrays even when single value from URL
        if (params.negotiationType && !Array.isArray(params.negotiationType)) {
            params.negotiationType = [params.negotiationType];
        }

        if (params.companySize && !Array.isArray(params.companySize)) {
            params.companySize = [params.companySize];
        }

        return params;
    }

    override onNextQueryParams(data: any): void {
        if (data.state && data.state !== this.lastStateLoaded) {
            this.loadCitiesForState(data.state);
        }
    }

    protected onStateChange(uf: string | null): void {
        if (uf) {
            this.loadCitiesForState(uf);
        } else {
            this.cities$ = of([]);
            this.filters.city = null;
            this.lastStateLoaded = null;
        }
    }

    private loadCitiesForState(state: string): void {
        this.cities$ = this.ibgeApiService.getCitiesByState(state).pipe(
            map((response: any) => response.data || [])
        );
        this.lastStateLoaded = state;
    }

    findAll(params: any): void {
        if (params.state && params.state !== this.lastStateLoaded) {
            this.loadCitiesForState(params.state);
        }

        this.companiesApiService.findAll(params)
            .subscribe({
                next: (response) => {
                    const data = response.data;
                    this.items = data.result || [];
                    this.totalAvailableForCampaign = data.totalAvailableForCampaign || 0;
                    this.pagination.itemsAmount = data.totalResults;
                    this.pagination.loading = false;
                },
                error: () => this.alertService.operationFailure()
            })
    }

    protected viewDebts(companyId: number): void {
        const modalRef = this.modalService.open(CompanyDebtsComponent, {
            size: 'fullscreen',
            centered: true,
            scrollable: true
        });

        modalRef.componentInstance.companyId = companyId;
    }

    protected viewDetails(companyId: number): void {
        const modalRef = this.modalService.open(CompanyDetailsComponent, {
            size: 'xl',
            centered: true,
            scrollable: true
        });

        modalRef.componentInstance.companyId = companyId;
    }

    protected goToCampaign(campaignId: number): void {
        this.router.navigate(['/campaigns', campaignId]);
    }

    protected getTaskStatusLabel(status: string): string {
        const labels: Record<string, string> = {
            'PENDING': 'Pendente',
            'IN_CONTACT': 'Em Contato',
            'PROPOSAL_SENT': 'Proposta Enviada',
            'UNDER_NEGOTIATION': 'Em Negociação',
            'CLOSED': 'Fechado',
            'LOST': 'Perdido'
        };
        return labels[status] || status;
    }

    protected getTaskStatusColor(status: string): string {
        const colors: Record<string, string> = {
            'PENDING': 'primary',
            'IN_CONTACT': 'info',
            'PROPOSAL_SENT': 'warning',
            'UNDER_NEGOTIATION': 'secondary',
            'CLOSED': 'success',
            'LOST': 'danger'
        };
        return colors[status] || 'secondary';
    }

    protected async copy(value: any) {
        await copyToClipboard(value);
        this.alertService.success('messages.success.label', 'messages.success.copy_to_clipboard');
    }

    protected enrichByDocumentNumber(documentNumber: string): void {
        if (!documentNumber) return;

        this.companiesApiService.enrich(documentNumber)
            .subscribe({
                next: () => {
                    this.alertService.successfulOperation();
                    this.search();
                },
                error: (_error) => {
                    if (_error.error.code === 'CNPJWS_RATE_LIMIT') {
                        this.alertService.error("Ops", `Limite de consultas atingido, aguarde 5 minutos para tentar novamente.`);
                        return;
                    }

                    this.alertService.operationFailure()
                }
            })
    }

    protected openCampaignDialog(): void {
        const modalRef = this.modalService.open(CampaignCreationDialogComponent, {
            size: 'xl',
            centered: true,
            backdrop: 'static'
        });

        // Passa os filtros atuais para o modal
        modalRef.componentInstance.filters = this.filters;
        modalRef.componentInstance.totalCompanies = this.pagination.itemsAmount;

        modalRef.result.then(
            (campaign: any) => {
                this.router.navigate(['/campaigns', campaign.id]);
            },
            () => {
                // Modal fechado sem criar
            }
        );
    }
}
